import { Component } from '@angular/core';

// ============================================================================
// Support kézikönyv — a support-szabalykonyv.txt (SOP v1.0 · 2026-07-07)
// teljes tartalma statikusan, a 09-support-kezikonyv.html struktúrájában.
// Viselkedés (keresés, kategória-szűrés, accordion, sablon-másolás):
// 23-logic.js kbVals()/kbArticles/kbCats.
// ============================================================================

interface KbTemplate {
  label: string;
  hu: string;
  en: string;
}

interface KbTable {
  headers: string[];
  rows: string[][];
}

interface KbArticle {
  id: string;
  cat: string;
  title: string;
  prio: 'P1' | 'P2' | 'P3' | null;
  tags: string[];
  rules: string[];
  table?: KbTable;
  templates: KbTemplate[];
}

interface KbCatChipVM {
  label: string;
  count: number;
  on: boolean;
  zero: boolean;
}

interface KbTemplateVM extends KbTemplate {
  key: string;
  huBtn: string;
  enBtn: string;
}

interface KbArticleVM {
  article: KbArticle;
  open: boolean;
  chevron: string;
  prioClass: string;
  templates: KbTemplateVM[];
}

const KB_CATS = [
  'Alapelvek & limitek',
  'Hol a csomagom?',
  'Elveszett csomag',
  'Sérült / hibás termék',
  'Rossz cím / újraküldés',
  'Visszatérítés / visszaküldés',
  'Módosítás / lemondás',
  'Számlázás',
  'Termékkérdés',
  'Panasz / értékelés',
];

const KB_ARTICLES: KbArticle[] = [
  {
    id: 'sla', cat: 'Alapelvek & limitek', title: 'Válaszidő (SLA) és alapelvek', prio: null,
    tags: ['sla', 'válaszidő', 'hangnem', 'etsy', 'dokumentálás', 'prioritás'],
    rules: [
      'Minden üzenetre 24 órán belül válaszolunk. Hétvégén (péntek 17:00 után) érkezőre legkésőbb hétfő nap végéig.',
      'Az első válasz mindig érdemi: ha nincs még megoldás, akkor is megírjuk, mit csinálunk és mikorra várható a következő lépés.',
      'A vevő sosem marad 3 munkanapnál tovább válasz nélkül — várakozásnál is státuszfrissítést küldünk.',
      'Hangnem: barátságos, rövid, megoldás-fókuszú. A vevőt sosem hibáztatjuk, a futárt csak tényszerűen említjük.',
      'Mindent dokumentálunk az ERP-ben: döntés, indoklás, elküldött üzenet a taskhoz csatolva.',
      'Etsy-szabály: Etsy-ügyeknél minden kommunikáció az Etsy üzenetküldőn megy (vitánál bizonyíték). Email csak számlaküldésre.',
      'P1 (kritikus): válasz 4 órán belül, megoldás 24 órán belül. P2: válasz 24 órán belül, megoldás 3 munkanap. P3: válasz 24 órán belül, megoldás 5 munkanap.',
    ],
    table: {
      headers: ['Prioritás', 'Mikor', 'Válaszidő', 'Megoldási cél'],
      rows: [
        ['P1 · Kritikus', 'Elveszett csomag, nem érkezett meg határidőre (pl. ajándék), Etsy case / negatív értékelés veszélye, dupla terhelés', '4 órán belül (munkaidőben)', '24 óra'],
        ['P2 · Standard', 'Sérült termék, rossz cím, csere, visszaküldés', '24 órán belül', '3 munkanap'],
        ['P3 · Alacsony', 'Számlakérés, általános kérdés, státuszérdeklődés', '24 órán belül', '5 munkanap'],
      ],
    },
    templates: [
      {
        label: '7.1 Első válasz, még nincs megoldás',
        hu: 'Kedves [név]! Köszönjük az üzenetét, megkaptuk. Már utánanézünk a(z) [#rendelésszám] rendelésének, és legkésőbb [nap] jelentkezünk a részletekkel. Üdvözlettel, [aláírás]',
        en: "Hi [name], thank you for reaching out! We've received your message and we're already looking into your order [#order number]. We'll get back to you with details by [day] at the latest. Best, [signature]",
      },
      {
        label: '7.11 Státuszfrissítés folyamatban lévő ügynél',
        hu: 'Kedves [név]! Csak jelezni szeretném, hogy az ügye nálunk van napirenden: [aktuális állapot]. A következő lépés: [mi és mikor]. Amint van új információ, azonnal írok!',
        en: "Hi [name], just a quick update to let you know your case is still being handled: [current status]. Next step: [what and when]. I'll write as soon as there's any news!",
      },
      {
        label: '7.12 Megoldás után — kedves lezárás',
        hu: 'Kedves [név]! Örülök, hogy sikerült megoldani. Ha bármi kérdése lenne a jövőben, írjon bátran. Köszönjük a türelmét és hogy nálunk vásárolt!',
        en: "Hi [name], I'm glad we could sort this out! If you have any questions in the future, don't hesitate to reach out. Thank you for your patience and for shopping with us!",
      },
    ],
  },
  {
    id: 'spring', cat: 'Alapelvek & limitek', title: 'Szállító: Spring GDS', prio: null,
    tags: ['spring gds', 'futár', 'kapcsolat', 'tracking', 'expressz', 'email'],
    rules: [
      'Kapcsolat: customerservice.hu@spring-gds.com — ide írunk, ha a csomag áll / elveszett (vizsgálat), címmódosítást kérünk, vagy visszaküldést intézünk.',
      'Tracking: a Spring GDS trackingje lassabban frissül, főleg az elosztóközpontokban — ez önmagában nem hiba, ne ijedj meg tőle.',
      'Ha a vevő a szállítás hosszát kifogásolja: magyarázd el, hogy ezzel a gazdaságos szállítási móddal tudjuk versenyképesen tartani az árainkat (sablon 7.13).',
      'Expressz szállítás kompenzációként mindenhova elérhető, kivéve az USA-t.',
    ],
    templates: [],
  },
  {
    id: 'komp', cat: 'Alapelvek & limitek', title: 'Kompenzáció-mátrix — mit adhatsz saját hatáskörben', prio: null,
    tags: ['kupon', 'kedvezmény', 'visszatérítés', 'limit', 'jóváhagyás', 'eszkaláció', 'attila'],
    rules: [
      'Kupon következő rendelésre: 10 € értékig saját hatáskör, felette Attila jóváhagyása kell.',
      'Részleges visszatérítés: a vételár 25%-áig saját hatáskör, felette Attila.',
      'Teljes visszatérítés: szabály szerinti esetben (sérült, elveszett, dupla fizetés, 1★) igen — szabályon kívül Attila.',
      'Ingyenes újragyártás + újraküldés: elveszett/törött csomagnál és 1★ értékelésnél saját hatáskör.',
      'Expressz szállítás upgrade: a mi hibánknál igen (USA-ba nem elérhető).',
      'Akciós árkülönbözet visszatérítése: nem — csak 10 € kupon gesztusként.',
      'Egyedi termék lemondásánál kedvezmény/kupon: nem adható (elvi szabály).',
      'Alapelv: a legolcsóbb, a vevőnek még jó megoldást ajánljuk először — de kis összegért nem megyünk vitába, ha a vita költsége nagyobb.',
    ],
    templates: [],
  },
  {
    id: 'eszk', cat: 'Alapelvek & limitek', title: 'Mikor kell task és mikor eszkalálunk Attilához?', prio: null,
    tags: ['task', 'eszkaláció', 'chargeback', 'jogi', 'visszaélés', 'attila'],
    rules: [
      'Task kell, ha: várunk valamire (Spring GDS, fotó, gyártás, döntés); pénzmozgás történik; az ügy több lépéses; határidőt kell követni.',
      'Nem kell task: egy üzenettel lezárható kérdés (tracking link, termékinfó).',
      'Ökölszabály: ami nem fér el a fejben és a mai napban, az task — semmi nem múlhat emlékezeten.',
      'Azonnali eszkaláció: limit feletti kompenzáció; jogi lépés / chargeback / Etsy case; adóügyi kérdés; visszaélés gyanúja; média/nagyker megkeresés; bármi, ami 5 perc után sem egyértelmű.',
      'Eszkalációnál a taskba: mi történt, mit kér a vevő, mit javasolsz te. A vevő addig is kap „utánanézünk” üzenetet — az SLA él!',
    ],
    templates: [],
  },
  {
    id: 'proaktiv', cat: 'Alapelvek & limitek', title: 'Mikor írunk proaktívan a vevőnek?', prio: null,
    tags: ['proaktív', 'csúszás', 'készlethiány', 'státuszfrissítés'],
    rules: [
      'Gyártási csúszás: azonnal, új határidővel.',
      'Tracking-probléma: ha mi vesszük észre, hogy a csomag 5+ napja áll — a vevő kérdezése előtt.',
      'Készlethiány a leadott rendelésnél: azonnal, alternatívával (másik anyag/szín, várakozás, visszatérítés).',
      'Futárvizsgálat / csere: 3 munkanaponta státuszfrissítés, akkor is, ha nincs új infó.',
      'Visszatérítés elindítva: megerősítő üzenet a várható jóváírási idővel.',
    ],
    templates: [],
  },
  {
    id: 'hol', cat: 'Hol a csomagom?', title: 'Státuszérdeklődés, tracking nem frissül', prio: 'P3',
    tags: ['tracking', 'szállítás', 'spring gds', 'lassú', 'státusz'],
    rules: [
      'Ellenőrizd a trackinget — a Spring GDS trackingje lassabban frissül, főleg elosztóközpontban. Az „álló” csomag gyakran csak nem frissült.',
      'Mozog a csomag: tracking link + várható kézbesítés. Pontos napot ne ígérj, csak a futár becslését add tovább.',
      'Lassúnak tartja: magyarázd el kedvesen a gazdaságos szállítás – versenyképes ár összefüggést (sablon 7.13).',
      '3–7 napja áll: válaszolj, hogy figyeljük + hozz létre taskot. A 7. napon írj a Spring GDS-nek (customerservice.hu@spring-gds.com).',
      '7+ napja áll: kezeld Elveszett csomagként.',
    ],
    templates: [
      {
        label: '7.2 Tracking / hol a csomagom',
        hu: 'Kedves [név]! Megnéztem a csomagját: jelenleg [státusz/hely], a futár becslése szerint [dátum] körül érkezik. Itt tudja követni: [tracking link]. Ha [dátum]-ig nem érkezne meg, mindenképp jelezze, és azonnal intézkedünk!',
        en: "Hi [name], I've checked your package: it's currently [status/location] and the carrier estimates delivery around [date]. You can track it here: [tracking link]. If it doesn't arrive by [date], please let us know and we'll take action right away!",
      },
      {
        label: '7.13 „Miért tart ilyen sokáig a szállítás?”',
        hu: 'Kedves [név]! Teljesen megértem a türelmetlenséget. Gazdaságos nemzetközi szállítási módot használunk — így tudjuk az árainkat versenyképesen tartani, cserébe a szállítás kicsit hosszabb, és a tracking az elosztóközpontokban lassabban frissül. A csomagja úton van: [aktuális státusz]. Ha [dátum]-ig nem érkezne meg, jelezze, és azonnal intézkedünk!',
        en: "Hi [name], I completely understand your impatience! We use an economical international shipping method — this is how we keep our prices competitive; in exchange, delivery takes a bit longer and tracking updates more slowly at distribution hubs. Your package is on its way: [current status]. If it doesn't arrive by [date], let us know and we'll take action right away!",
      },
    ],
  },
  {
    id: 'elveszett', cat: 'Elveszett csomag', title: 'Elveszett csomag — újraküldés, nem visszatérítés', prio: 'P1',
    tags: ['elveszett', 'tracking', 'vizsgálat', 'újraküldés', 'spring gds', 'kézbesítve'],
    rules: [
      '7+ nap mozdulatlan tracking = elveszettnek tekintjük; nem várjuk meg a futár hivatalos lezárását a vevő kárára.',
      'Azonnal vizsgálat a Spring GDS-nél (customerservice.hu@spring-gds.com) + task.',
      'Alapmegoldás: ingyenes újragyártás + újraküldés. Visszatérítést magunktól SOHA nem ajánlunk — a lemondás rontja a bolt statisztikáit.',
      'Ha a vevő kifejezetten visszatérítést kér: add meg (egy Etsy-vitát úgyis elveszítenénk), de előbb egyszer még ajánld az újraküldést.',
      'A futárkártérítés a mi ügyünk — a vevőt nem várakoztatjuk rá.',
      '„Kézbesítve”, de nem kapta meg: szomszédok/lerakóhely + 2 munkanap várakozás, utána újraküldés. Gyanús ismétlődés ugyanattól a vevőtől → eszkaláció.',
    ],
    templates: [
      {
        label: '7.3 Újraküldés felajánlása (visszatérítést NEM ajánlunk!)',
        hu: 'Kedves [név]! Sajnálom, hogy ennyit kell várnia — a tracking alapján a csomag valóban elakadt, ezért vizsgálatot indítottunk a szállítónál. Önt viszont nem várakoztatjuk emiatt: ingyen újra elkészítjük és elküldjük a rendelését. Amint feladtuk, küldöm az új követési számot!',
        en: "Hi [name], I'm so sorry about the wait — based on the tracking, your package does appear to be stuck, so we've opened an investigation with the carrier. But we won't make you wait for that: we'll remake and reship your order free of charge. I'll send you the new tracking number as soon as it's on its way!",
      },
    ],
  },
  {
    id: 'serult', cat: 'Sérült / hibás termék', title: 'Sérült / törött / hibás termék', prio: 'P2',
    tags: ['sérült', 'törött', 'fotó', 'csere', 'részleges visszatérítés', 'futárkár', 'kupon'],
    rules: [
      'Mindig kérünk fotót (termék + csomagolás) — kell a futárkárigényhez és kizárja a visszaélést.',
      'Kis esztétikai hiba, használható termék: választás — részleges visszatérítés (15–25%) VAGY csere. A legtöbb vevő a részlegest választja, nekünk is olcsóbb.',
      'Használhatatlan / törött: ingyenes csere, a sérült terméket NEM kérjük vissza. Ha inkább pénzt kér: teljes visszatérítés.',
      'Rossz terméket küldtünk (mi hibánk): azonnali ingyenes csere expressz opcióval (USA-ba csak standard), bocsánatkérés + 10 € kupon.',
      'Fotó nélkül nincs kompenzáció. Két kérés után udvariasan zárjuk.',
    ],
    templates: [
      {
        label: '7.4 Fotó bekérése',
        hu: 'Kedves [név]! Nagyon sajnálom, hogy sérülten érkezett a csomag! Ahhoz, hogy azonnal intézkedni tudjunk (és a futárnál is kárigényt indítsunk), kérem, küldjön pár fotót a termékről és a csomagolásról is. Amint megvannak, rögtön megoldjuk!',
        en: "Hi [name], I'm so sorry your order arrived damaged! So we can resolve this right away (and file a claim with the carrier), could you please send a few photos of the product and the packaging? As soon as we have them, we'll sort it out immediately!",
      },
      {
        label: '7.5 Részleges visszatérítés vagy csere',
        hu: 'Köszönöm a fotókat, [név]! Teljesen jogos a jelzése. Két megoldást ajánlok: 1) visszatérítjük a vételár [X]%-át, és a terméket természetesen megtarthatja, vagy 2) ingyen elkészítjük és elküldjük újra. Melyik lenne jobb Önnek?',
        en: "Thank you for the photos, [name]! You're absolutely right to flag this. I can offer two solutions: 1) we refund [X]% of the purchase price and you keep the item, or 2) we remake and reship it free of charge. Which works better for you?",
      },
    ],
  },
  {
    id: 'nemolyan', cat: 'Sérült / hibás termék', title: '„Nem olyan, mint a képen” / nyomtatási minőség', prio: 'P3',
    tags: ['nyomtatás', 'szín', 'fa', 'erezet', 'természetes', 'minőség', 'kupon'],
    rules: [
      'Első lépés mindig: fotó bekérése.',
      'Valós hiba (nyomtatási hiba, rossz szín, elcsúszott minta): kezeld sérült termékként (csere vagy részleges visszatérítés).',
      'Természetes eltérés (fa erezete, árnyalatnyi színkülönbség): kedves magyarázat (7.15) — kompenzáció alapból nincs.',
      'Szubjektív elégedetlenség + értékelés-kockázat: 10 € kupon felajánlható saját hatáskörben, vagy a 30 napos visszaküldés.',
    ],
    templates: [
      {
        label: '7.15 Természetes eltérés — magyarázat',
        hu: 'Kedves [név]! Köszönöm, hogy jelezte. Termékeink természetes fából, kézzel készülnek, ezért minden darab erezete és árnyalata kicsit egyedi — nincs két teljesen egyforma. A fotóinkon egy-egy konkrét példány látható, az Ön darabja ugyanabból az anyagból, ugyanazzal a gondossággal készült. Ha viszont konkrét hibát lát rajta (nyomtatás, sérülés), kérem, küldjön fotót, és azonnal megoldjuk!',
        en: "Hi [name], thank you for letting us know! Our products are handmade from natural wood, so the grain and shade of every piece is slightly unique — no two are exactly alike. Our photos show one specific piece; yours was made from the same material with the same care. However, if you see an actual defect (printing, damage), please send a photo and we'll resolve it immediately!",
      },
    ],
  },
  {
    id: 'cim', cat: 'Rossz cím / újraküldés', title: 'Rossz cím, visszajött csomag, címmódosítás', prio: 'P2',
    tags: ['cím', 'újraküldés', 'átirányítás', 'spring gds', 'komment', 'erp'],
    rules: [
      'A vevő írta el: az újraküldés szállítási díját a vevő fizeti (a terméket nem számoljuk fel újra). Kedvesen, de egyértelműen.',
      'Mi címkéztük rosszul: ingyenes újraküldés + bocsánatkérés.',
      'Úton lévő csomagnál: előbb címmódosítás-kérés a Spring GDS-től — nem garantált, de gyakran működik.',
      'Visszajött csomagnál újraküldés előtt mindig ellenőriztesd a teljes címet a vevővel.',
      'Ha még nincs feladva: az ERP-ben átírjuk a címet + komment a rendeléshez: „Cím megváltozott, átírtam: [új cím]”. Módosítás komment nélkül nem létezik!',
    ],
    templates: [
      {
        label: '7.6 Vevő hibája — díjköteles újraküldés',
        hu: 'Kedves [név]! A csomag visszaérkezett hozzánk, mert a megadott címben sajnos hibás volt a [házszám/utca]. Örömmel újraküldjük — a terméket természetesen nem számoljuk fel újra, csak az újraküldés szállítási díját, ami [összeg]. Kérem, erősítse meg a pontos, teljes címet, és máris indítjuk!',
        en: "Hi [name], your package was returned to us because the [house number/street] in the address was unfortunately incorrect. We're happy to reship it — of course we won't charge for the product again, only the reshipping fee of [amount]. Please confirm your full, correct address and we'll send it right out!",
      },
    ],
  },
  {
    id: 'vissza', cat: 'Visszatérítés / visszaküldés', title: 'Visszaküldés és visszatérítés szabályai', prio: 'P2',
    tags: ['visszaküldés', 'visszatérítés', 'elállás', '30 nap', 'egyedi', 'gravírozás'],
    rules: [
      'Standard termék: visszaküldés kézbesítéstől 30 napon belül. A díját a vevő fizeti; csak a termék árát térítjük (szállítást nem), sértetlen beérkezés után 5 munkanapon belül.',
      'A visszaküldést a Spring GDS-en keresztül intézzük (customerservice.hu@spring-gds.com).',
      'Egyedi / személyre szabott termék: elállást NEM fogadunk el (jogilag is kivétel). Kivéve, ha hibás — az a sérült termék szabály szerint megy.',
      'Használtan / sérülten visszaküldött termék: részleges visszatérítés az állapot arányában, vagy elutasítás — eszkaláció Attilához.',
      'Visszatérítés mindig az eredeti fizetési módra — sosem más számlára.',
    ],
    templates: [
      {
        label: '7.7 Elállás elutasítása — egyedi termék',
        hu: 'Kedves [név]! Köszönöm az üzenetét. Sajnos ezt a rendelést nem tudjuk visszavenni, mert a termék egyedi, személyre szabottan készült ([gravírozás/egyedi méret]), így máshol nem tudjuk értékesíteni — a személyre szabott termékek a vásárlási feltételeink (és az uniós szabályozás) szerint is kivételt képeznek az elállási jog alól. Ha bármilyen hiba van a termékkel, azt természetesen javítjuk vagy cseréljük — kérem, jelezze!',
        en: "Hi [name], thank you for your message. Unfortunately we're unable to accept a return for this order, as the item was custom-made for you ([engraving/custom size]) and can't be resold — personalized items are exempt from the right of withdrawal under our shop policies (and EU regulations). That said, if there is any defect with the product, we'll of course repair or replace it — just let us know!",
      },
    ],
  },
  {
    id: 'lemondas', cat: 'Módosítás / lemondás', title: 'Rendelés lemondása — a feladás ténye dönt', prio: 'P2',
    tags: ['lemondás', '2 óra', 'etsy', 'feladás', 'egyedi', 'visszatérítés'],
    rules: [
      'Etsy-szabály: a vevőnek 2 órája van lemondani — ezen belül kérdés nélkül, teljes visszatérítéssel.',
      '2 órán túl az ELSŐ lépés MINDIG: feladtuk-e már a csomagot? A feladás ténye dönt, nem a 2 óra.',
      'Még NEM adtuk fel: méltányosságból elfogadjuk, teljes visszatérítés — a gyártást/csomagolást azonnal állítsd le.',
      'Már FELADTUK: lemondás nem lehetséges (sablon 7.14) — 30 napos visszaküldés él.',
      'Egyedi / személyre szabott termék: lemondást NEM fogadunk el a 2 órás ablakon túl, akkor sem, ha nincs feladva. Kedvezményt, kupont sem ajánlunk (sablon 7.8).',
    ],
    templates: [
      {
        label: '7.8 Lemondás elutasítása — egyedi termék',
        hu: 'Kedves [név]! Köszönöm az üzenetét. Sajnos ezt a rendelést nem tudjuk lemondani: a termék egyedileg, kifejezetten az Ön számára készül ([gravírozás/egyedi méret]), így máshol nem tudnánk értékesíteni. A személyre szabott rendelések a vásárlási feltételeink szerint a leadás után nem mondhatók le. Sok szeretettel készítjük — bízom benne, hogy kézhez véve megszereti majd!',
        en: "Hi [name], thank you for your message. Unfortunately we're unable to cancel this order: the item is being custom-made specifically for you ([engraving/custom size]), so we couldn't sell it elsewhere. As per our shop policies, personalized orders cannot be cancelled once placed. It's being made with great care — I'm confident you'll love it once it arrives!",
      },
      {
        label: '7.14 Lemondás elutasítása — már feladtuk',
        hu: 'Kedves [név]! Köszönöm az üzenetét. Sajnos a rendelését már nem tudjuk lemondani, mert a csomagot feladtuk, és már úton van Önhöz. Amit ilyenkor tehetünk: az átvétel után 30 napon belül visszaküldheti a saját költségén, és amint sértetlenül megérkezik hozzánk, a termék árát visszatérítjük. De bízom benne, hogy ha kézhez veszi, meggondolja magát — sok szeretettel készült!',
        en: "Hi [name], thank you for your message. Unfortunately we can no longer cancel your order, as the package has already been shipped and is on its way to you. What we can do: you may return it within 30 days of delivery at your own cost, and once it arrives back to us undamaged, we'll refund the product price. That said, I hope you'll change your mind once you see it — it was made with a lot of care!",
      },
    ],
  },
  {
    id: 'modositas', cat: 'Módosítás / lemondás', title: 'Rendelés módosítása az ERP-ben (cím, tételek)', prio: 'P2',
    tags: ['módosítás', 'komment', 'erp', 'tétel', 'cím', 'csomagolt'],
    rules: [
      'Minden módosítást az ERP-ben vezetünk át, és MINDIG komment megy a rendelés mellé — enélkül a gyártás/csomagolás nem tud róla!',
      'Címváltozás: cím átírása + komment: „Cím megváltozott, átírtam: [új cím]”.',
      'Tétel törlése: tétel kivétele + komment: „[tétel] törölve a vevő kérésére”. A különbözetet visszatérítjük.',
      'Tétel hozzáadása: hozzáadás + komment: „[tétel] hozzáadva a vevő kérésére”. A különbözetet a vevő kifizeti.',
      'Honnan tudod, hogy késő? Ha a rendeléshez már létrejött a csomag (csomagolt státusz), a módosítás nem lehetséges — tételnél új rendelés kell, címnél Spring GDS-átirányítás, lemondásnál a 30 napos szabály él.',
      'Címmódosítás feladás előtt: ingyenes, azonnal. Feladás után: átirányítás-kérés (nem garantált).',
    ],
    templates: [],
  },
  {
    id: 'szamla', cat: 'Számlázás', title: 'Áfás számla, dupla fizetés', prio: 'P3',
    tags: ['számla', 'áfa', 'adószám', 'vies', 'billingo', 'dupla fizetés'],
    rules: [
      'Áfás/céges számla utólag: kérd be a cégnevet, címet, adószámot (EU-s vevőnél közösségi adószám — ellenőrizd VIES-ben). Billingóban javítjuk és emailben küldjük.',
      'Dupla fizetés: ellenőrzés után a második tranzakciót kérdés nélkül, azonnal visszatérítjük + várható jóváírási idő (5–10 banki nap).',
      'Amit nem tudsz megválaszolni → eszkaláció Attilához. Adóügyben NEM improvizálunk.',
    ],
    templates: [
      {
        label: '7.9 Áfás számla — adatok bekérése',
        hu: 'Kedves [név]! Természetesen kiállítjuk a céges számlát. Kérem, küldje el: a pontos cégnevet, a székhely címét és az (EU-s) adószámot. Amint megvannak, 1–2 munkanapon belül emailben küldjük a számlát.',
        en: "Hi [name], of course, we're happy to issue a business invoice. Please send us: the exact company name, the registered address, and the (EU) VAT number. Once we have these, we'll email you the invoice within 1–2 business days.",
      },
      {
        label: '7.10 Dupla fizetés — visszatérítés megerősítése',
        hu: 'Kedves [név]! Igaza van, a rendszerünkben is látszik a dupla tranzakció — elnézést kérünk a kellemetlenségért! A második terhelést már vissza is térítettük; a bankjától függően 5–10 munkanapon belül jelenik meg a számláján.',
        en: "Hi [name], you're right — we can see the duplicate transaction on our end as well, and we're sorry for the inconvenience! We've already refunded the second charge; depending on your bank, it should appear on your account within 5–10 business days.",
      },
    ],
  },
  {
    id: 'termekkerdes', cat: 'Termékkérdés', title: 'Vásárlás előtti kérdés, egyedi rendelés kérése', prio: 'P3',
    tags: ['egyedi rendelés', 'nagyker', 'céges', 'kapacitás', 'bevétel'],
    rules: [
      '24 órán belül válaszolunk — a gyors válasz itt közvetlen bevétel.',
      'Egyedi megrendelést alapból NEM vállalunk, de mérlegelj:',
      'Egyetlen kisebb egyedi darab: udvarias elutasítás (7.17) + a standard kínálat ajánlása.',
      'Nagyobb példányszám, céges/nagyker vagy nagy értékű kérés: NE utasítsd el — kérd be a részleteket (mennyiség, méret, határidő) és eszkaláld Attilának.',
      'Sose ígérj olyan határidőt vagy kivitelezést, amit a gyártás nem igazolt vissza.',
    ],
    templates: [
      {
        label: '7.17 Egyedi rendelés — udvarias elutasítás',
        hu: 'Kedves [név]! Köszönjük a megkeresést és a bizalmat! Sajnos egyedi megrendeléseket jelenleg nem tudunk vállalni, mert a gyártási kapacitásunk a standard termékeinkre van beosztva. Ha érdekli, itt találja a teljes kínálatunkat: [link] — talán van köztük olyan, ami közel áll ahhoz, amit elképzelt!',
        en: "Hi [name], thank you for reaching out and for your trust! Unfortunately we're unable to take on custom orders at the moment, as our production capacity is dedicated to our standard products. If you're interested, you can find our full range here: [link] — you might find something close to what you had in mind!",
      },
    ],
  },
  {
    id: 'panasz', cat: 'Panasz / értékelés', title: 'Negatív értékelés, értékeléssel fenyegetés', prio: 'P1',
    tags: ['1 csillag', 'értékelés', 'panasz', 'etsy case', 'kompenzáció'],
    rules: [
      'Értékeléssel fenyeget: P1, a megoldást egy szinttel bőkezűbben mérjük — de csak valós probléma esetén.',
      '1★ értékelés érkezett: a legmagasabb prioritás az egész supportban. Azonnali kapcsolatfelvétel + teljes csomag: teljes visszatérítés ÉS ingyenes újraküldés ÉS ajándék — veszteség árán is.',
      'Értékelés-módosítást kompenzációért cserébe SOSEM kérünk (Etsy-szabályba ütközik). Előbb megoldjuk feltétel nélkül, utána jelezhetjük udvariasan, hogy örülnénk a frissítésnek.',
    ],
    templates: [
      {
        label: '7.16 1 csillagos értékelés után — helyreállítás',
        hu: 'Kedves [név]! Láttam az értékelését, és nagyon sajnálom, hogy ilyen élménye volt velünk — ez nem az a színvonal, amit nyújtani szeretnénk. Szeretném rendbe hozni: a teljes vételárat visszatérítjük, ÉS ingyen újra elküldjük a rendelését egy kis ajándékkal együtt. Nem kérünk cserébe semmit — csak szeretnénk, ha végül elégedett lenne. Elfogadja?',
        en: "Hi [name], I saw your review and I'm truly sorry your experience was like this — it's not the standard we want to deliver. I'd like to make it right: we'll refund your full purchase price AND reship your order free of charge, along with a small gift. We're not asking for anything in return — we just want you to be happy in the end. Would that be okay?",
      },
    ],
  },
  {
    id: 'akcio', cat: 'Panasz / értékelés', title: 'Akciós árkülönbözet visszakérése', prio: 'P3',
    tags: ['akció', 'árkülönbözet', 'kupon', 'leárazás'],
    rules: [
      'Alapszabály: az árkülönbözetet nem térítjük vissza — az akció az akció alatt leadott rendelésekre vonatkozik (sablon 7.18).',
      'Méltányosság: ha a rendelés az akció indulása előtti 24 órán belül érkezett → 10 € kupon a következő rendelésre (gesztus, nem árkorrekció).',
      'Tényleges különbözet-visszatérítés csak Attila jóváhagyásával (nagy összeg, értékelés-kockázat).',
    ],
    templates: [
      {
        label: '7.18 Elutasítás (+ opcionális kupon)',
        hu: 'Kedves [név]! Köszönöm az üzenetét, és megértem a kérdést. Az akciós áraink az akció ideje alatt leadott rendelésekre vonatkoznak, ezért a különbözetet sajnos nem tudjuk visszatéríteni. [Ha <24 órával az akció előtt rendelt:] Mivel viszont épp csak lemaradt róla, szeretnék adni egy 10 eurós kupont a következő rendeléséhez: [kuponkód]. Köszönjük a megértését!',
        en: "Hi [name], thank you for your message, and I understand the question! Our sale prices apply to orders placed during the sale period, so unfortunately we're unable to refund the difference. [If ordered <24h before the sale:] However, since you only just missed it, I'd like to give you a €10 coupon for your next order: [coupon code]. Thank you for your understanding!",
      },
    ],
  },
  {
    id: 'gyorstabla', cat: 'Alapelvek & limitek', title: 'Gyors döntési tábla (függelék)', prio: null,
    tags: ['döntési tábla', 'függelék', 'gyors', 'kompenzáció', 'cselekvés'],
    rules: [],
    table: {
      headers: ['Helyzet', 'Azonnali cselekvés', 'Kompenzáció'],
      rows: [
        ['Tracking 7+ napja áll', 'email a Spring GDS-nek + üzenet a vevőnek', 'ingyenes újragyártás + újraküldés (visszatérítés CSAK ha a vevő kéri)'],
        ['„Miért ilyen lassú a szállítás?”', 'sablon 7.13', 'nincs — gazdaságos szállítás = versenyképes ár'],
        ['Törött termék (fotóval)', 'csere indítása, terméket nem kérjük vissza', 'csere VAGY teljes visszatérítés'],
        ['Kis esztétikai hiba (fotóval)', 'ajánlat küldése', '15–25% részleges visszatérítés VAGY csere'],
        ['Sérülés, fotó nincs', 'fotó bekérése (max 2x)', 'fotó nélkül nincs'],
        ['„Nem olyan, mint a képen” / nyomtatás', 'fotó bekérése', 'valós hiba → 3.3; természetes eltérés → magyarázat (7.15), max 10 € kupon'],
        ['Címváltozás, nincs feladva', 'ERP-ben cím átírása + komment a rendeléshez', '—'],
        ['Címhiba, már feladva', 'Spring GDS átirányítás kérése; ha visszajön: cím megerősítése', 'újraküldés vevő költségén, ha a vevő hibázott'],
        ['Tétel törlés/hozzáadás, nincs feladva', 'ERP-ben módosítás + komment a rendeléshez', 'különbözet visszatérítése / bekérése'],
        ['Tétel módosítás, csomag már létrehozva', 'nem lehetséges — új rendelés kell', '—'],
        ['Lemondás, csomag MÉG NINCS feladva', 'gyártás/csomagolás leállítása', 'teljes visszatérítés (2 órán túl is, méltányosságból)'],
        ['Lemondás, csomag MÁR feladva', 'elutasítás (7.14)', '30 napos visszaküldés saját költségen, termék ára sértetlen beérkezés után'],
        ['Lemondás, egyedi termék', 'elutasítás (7.8)', 'nincs — sem kedvezmény, sem kupon'],
        ['Visszaküldés, standard termék, <30 nap', 'visszaküldés egyeztetése (Spring GDS)', 'csak a termék ára, sértetlen beérkezés után; szállítást a vevő fizeti'],
        ['Visszaküldés, egyedi termék', 'udvarias elutasítás (7.7)', 'nincs (kivéve hiba)'],
        ['Akciós különbözetet kér', 'rendelés időpontjának ellenőrzése', 'nincs; ha <24 órával az akció előtt rendelt: 10 € kupon (7.18)'],
        ['Dupla fizetés', 'azonnali visszatérítés', 'teljes második tranzakció'],
        ['Áfás számla kérés', 'adatok bekérése, VIES-ellenőrzés', '—'],
        ['Egyedi rendelés kérése', 'kicsi → elutasítás (7.17); nagy tétel → eszkaláció Attilának', '—'],
        ['Értékeléssel fenyeget', 'P1', 'egy szinttel bőkezűbb, ha valós a panasz'],
        ['1★ értékelés érkezett', 'LEGMAGASABB prioritás, azonnali üzenet (7.16)', 'teljes visszatérítés + újraküldés + ajándék, veszteség árán is'],
      ],
    },
    templates: [],
  },
];

@Component({
  selector: 'app-support-kezikonyv',
  templateUrl: './support-kezikonyv.component.html',
  styleUrl: './support-kezikonyv.component.scss'
})
export class SupportKezikonyvComponent {
  search = '';
  cat = 'Mind';

  catChips: KbCatChipVM[] = [];
  list: KbArticleVM[] = [];
  resultLabel = '';
  empty = false;

  private openMap: Record<string, boolean> = {};
  private copiedId: string | null = null;
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.rebuild();
  }

  onSearchChange(value: string): void {
    this.search = value;
    // Új keresés: a korábbi kézi nyit/zár állapotok elévülnek — a találatok
    // alapból nyitva jelennek meg (rebuild dönt a `searching` alapján).
    this.openMap = {};
    this.rebuild();
  }

  pickCat(cat: string): void {
    this.cat = cat;
    this.rebuild();
  }

  toggle(vm: KbArticleVM): void {
    this.openMap[vm.article.id] = !vm.open;
    this.rebuild();
  }

  copy(templateVm: KbTemplateVM, lang: 'hu' | 'en'): void {
    const id = `${templateVm.key}-${lang}`;
    const text = lang === 'hu' ? templateVm.hu : templateVm.en;
    try {
      navigator.clipboard?.writeText(text).catch(() => {
        // vágólap nem elérhető — a gomb-visszajelzés így is megjelenik
      });
    } catch (e) {
      // vágólap nem elérhető — a gomb-visszajelzés így is megjelenik
    }
    this.copiedId = id;
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
    }
    this.copyTimer = setTimeout(() => {
      this.copiedId = null;
      this.rebuild();
    }, 1600);
    this.rebuild();
  }

  trackArticle(_: number, vm: KbArticleVM): string {
    return vm.article.id;
  }

  trackChip(_: number, chip: KbCatChipVM): string {
    return chip.label;
  }

  // --------------------------------------------------------------------------
  // Nézet újraépítése (23-logic.js kbVals mintájára)
  // --------------------------------------------------------------------------

  private rebuild(): void {
    const q = this.search.trim().toLowerCase();
    const searching = q.length >= 2;

    const matches = (article: KbArticle): boolean => {
      if (!q) {
        return true;
      }
      const hay = [
        article.title,
        article.cat,
        article.tags.join(' '),
        article.rules.join(' '),
        article.table ? [...article.table.headers, ...article.table.rows.flat()].join(' ') : '',
        article.templates.map((t) => `${t.label} ${t.hu} ${t.en}`).join(' '),
      ].join(' ').toLowerCase();
      return q.split(/\s+/).every((word) => hay.includes(word));
    };

    const searchPool = KB_ARTICLES.filter(matches);
    const filtered = searchPool.filter((article) => this.cat === 'Mind' || article.cat === this.cat);

    this.catChips = ['Mind', ...KB_CATS].map((cat) => {
      const count = cat === 'Mind'
        ? searchPool.length
        : searchPool.filter((article) => article.cat === cat).length;
      return { label: cat, count, on: this.cat === cat, zero: count === 0 };
    });

    this.empty = filtered.length === 0;
    this.resultLabel = searching
      ? `${filtered.length} találat a keresésre`
      : `${filtered.length} bejegyzés`;

    this.list = filtered.map((article) => {
      // Kereséskor a találatok alapból nyitva vannak, de a felhasználó
      // becsukhatja őket (a kézi állapot felülírja az alapértelmezést).
      const open = this.openMap[article.id] ?? searching;
      return {
        article,
        open,
        chevron: open ? '▾' : '▸',
        prioClass: article.prio ? `kb-prio--${article.prio.toLowerCase()}` : '',
        templates: article.templates.map((template, index) => {
          const key = `${article.id}-t${index}`;
          return {
            ...template,
            key,
            huBtn: this.copiedId === `${key}-hu` ? 'Másolva ✓' : 'HU másolása',
            enBtn: this.copiedId === `${key}-en` ? 'Másolva ✓' : 'EN másolása',
          };
        }),
      };
    });
  }
}
